/* Mori campaign 658: one delayed welcome offer on the homepage per tab visit.
 * Keep Mori's native time/exit/scroll triggers OFF. Mori owns email submission
 * and Shopify customer sync; this adapter only schedules its public opener.
 */
(() => {
  'use strict';
  if (window.Shopify?.designMode) return;
  if (location.pathname !== '/') return;
  const campaign = 658;
  const completedKey = `sp_subscribed_${campaign}`;
  const shownKey = `fv_reward_shown_${campaign}`;
  const firstOpenDelay = 60 * 1000;
  const cartReleaseDelay = 8000;
  const dependencyTimeout = 120 * 1000;
  let timer;
  let nextAt = 0;
  let cartPauseUntil = 0;
  let attached = false;
  const read = (storage, key) => { try { return storage.getItem(key); } catch { return null; } };
  const completed = () => read(localStorage, completedKey) === '1';
  const shown = () => read(sessionStorage, shownKey) === '1';
  const wait = delay => new Promise(resolve => setTimeout(resolve, delay));

  async function loadConfig() {
    for (let attempt = 0; attempt < 6 && !completed() && !shown(); attempt += 1) {
      try {
        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), 5000);
        const response = await fetch('/apps/scratch/popup-config', {
          cache: 'no-store',
          signal: controller.signal
        });
        clearTimeout(abortTimer);
        if (response.ok) {
          const data = await response.json();
          const configs = data.configs || [data.config];
          if (configs.length === 1) return configs[0];
          return null;
        }
      } catch { /* retry slow or interrupted storefront requests */ }
      await wait(250 * (attempt + 1));
    }
    return null;
  }

  async function waitForMori() {
    const deadline = Date.now() + dependencyTimeout;
    while (!completed() && !shown() && Date.now() < deadline) {
      const popup = document.getElementById('scratch-popup');
      const card = popup?.querySelector('.sp-card');
      if (popup && typeof window.__scratchPopup?.openFromTeaser === 'function' &&
          card?.style.getPropertyValue('--sp-popup-bg')) return popup;
      await wait(100);
    }
    return null;
  }

  async function start() {
    if (completed() || shown()) return;
    const [config, popup] = await Promise.all([loadConfig(), waitForMori()]);
    if (!config || !popup || attached || completed() || shown()) return;
    // Fail closed if a different campaign is introduced; don't open the wrong offer.
    if (!config.enabled || Number(config.campaignId) !== campaign || !config.requireEmail) return;
    if (config.pages !== 'all' && location.pathname !== '/') return;
    if (config.urlTargeting !== 'all' || config.deviceTarget !== 'all') return;
    attached = true;
    document.dispatchEvent(new CustomEvent('fv:reward-ready', { detail: config }));
    // Only one offer per tab visit, even if the visitor closes it or reloads home.
    nextAt = Date.now() + firstOpenDelay;
    popup.addEventListener('close', () => {
      clearTimeout(timer);
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) schedule();
    });
    window.addEventListener('pageshow', schedule);
    window.addEventListener('storage', event => {
      if (event.key === completedKey && completed()) {
        clearTimeout(timer);
        if (popup.open) window.__scratchPopup.close();
      }
    });
    const cartIsActive = () => {
      const cart = document.querySelector('[data-cart-drawer-root]');
      return document.body.classList.contains('fv-cart-drawer-open') ||
        cart?.classList.contains('is-open') || cart?.classList.contains('is-updating');
    };
    function pauseForCart() {
      cartPauseUntil = Math.max(cartPauseUntil, Date.now() + cartReleaseDelay);
      nextAt = Math.max(nextAt, cartPauseUntil);
      clearTimeout(timer);
      if (popup.open) {
        window.__scratchPopup.close();
        return;
      }
      schedule();
    }
    document.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[name="add"], [data-cart-drawer-open], [data-cart-drawer-close], [data-cart-quantity-change], [data-cart-remove]')) {
        pauseForCart();
      }
    }, true);
    document.addEventListener('submit', event => {
      const form = event.target instanceof Element ? event.target.closest('form') : null;
      if ((form?.getAttribute('action') || '').includes('/cart/add')) pauseForCart();
    }, true);
    document.addEventListener('fanniverse:cart-updated', pauseForCart);
    function schedule() {
      clearTimeout(timer);
      if (completed() || shown() || popup.open || document.hidden) return;
      const now = Date.now();
      if (cartIsActive()) {
        cartPauseUntil = Math.max(cartPauseUntil, now + cartReleaseDelay);
        nextAt = Math.max(nextAt, cartPauseUntil);
        timer = setTimeout(schedule, 1000);
        return;
      }
      const delay = Math.max(nextAt, cartPauseUntil) - now;
      if (delay > 0) { timer = setTimeout(schedule, delay); return; }
      // Do not interrupt another modal (e.g. a cart dialog).
      if (document.querySelector('dialog[open]')) { timer = setTimeout(schedule, 1500); return; }
      window.__scratchPopup.openFromTeaser();
      if (popup.open) {
        try { sessionStorage.setItem(shownKey, '1'); } catch { /* storage may be unavailable */ }
      }
    }
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
